import { useState, useEffect, useCallback } from 'react';
import { supportsWebP } from '../utils/webpSupport';

const loadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(src);
    img.onerror = reject;
    img.src = src;
  });
};

export const useImagePreloader = (images = []) => {
  const [loadedImages, setLoadedImages] = useState({});
  const [isWebPSupported] = useState(() => supportsWebP());

  const preloadImages = useCallback(async () => {
    const imagePromises = images.map(async ({ webp, fallback, key }) => {
      const primarySrc = isWebPSupported ? webp : fallback;
      
      try {
        await loadImage(primarySrc);
        return { key, loaded: true, src: primarySrc };
      } catch {
        if (isWebPSupported && webp !== fallback) {
          try {
            await loadImage(fallback);
            return { key, loaded: true, src: fallback };
          } catch {
            return { key, loaded: false, src: null };
          }
        }
        return { key, loaded: false, src: null };
      }
    });

    const results = await Promise.allSettled(imagePromises);
    const loadedStates = {};
    
    results.forEach((result) => {
      if (result.status === 'fulfilled') {
        const { key, loaded } = result.value;
        loadedStates[key] = loaded;
      }
    });

    setLoadedImages(loadedStates);
  }, [images, isWebPSupported]);

  useEffect(() => {
    if (images.length > 0) {
      preloadImages();
    }
  }, [preloadImages, images.length]);

  return { loadedImages, isWebPSupported };
};